import { birthdateToDate } from './birthdate';

/**
 * O(1) duplicate-patient lookup for the Add Patient flow.
 *
 * SHER-194: the previous implementation called `.find()` over the full
 * decrypted patient list on every submit, doing two console.log and four
 * string normalisations per patient. On CHOM's 49,992-patient DB that was
 * ~30-60s per submit (worse with DevTools open).
 *
 * This module builds an index once per Redux patients-array reference
 * (via useMemo in the caller) and looks up candidates in O(1) at submit.
 */

export interface DedupCandidate {
    uuid: string;
    personalData: {
        name?: string;
        surnames?: string;
        birthdate?: any;
        health_id?: string;
        [k: string]: any;
    };
}

export interface DedupIndex {
    /** Map<normalizedHealthId, patient> — fast path; empty when no patient has a health_id. */
    byHealthId: Map<string, DedupCandidate>;
    /** Map<`${name}|${surname}|${dobKey}`, patient[]> — fallback when health_id is missing. */
    byNameDob: Map<string, DedupCandidate[]>;
    /**
     * SHER-257: Map<normalizedPhone, patient[]> across every phone-typed personal
     * field (primary `phone` + tenant-custom secondaries). Empty when no
     * phoneFieldNames are supplied (backward compatible with pre-SHER-257 callers).
     */
    byPhone: Map<string, DedupCandidate[]>;
    /**
     * SHER-257 (follow-up): Map<normalizedIdCard, patient[]> across every
     * ID-card-typed field (e.g. hospices' National ID card). Empty when no
     * idCardFieldNames are supplied (CHOM, phone-only, is unaffected).
     */
    byIdCard: Map<string, DedupCandidate[]>;
}

/**
 * Reason a candidate is flagged as a possible duplicate.
 * `name_dob_approx`: same name+surname with a DOB off by ±1 day (data-entry typo).
 * `id_card`: same National ID card — fires on its own, regardless of name/DOB.
 */
export type DedupReason = 'health_id' | 'name_dob' | 'name_dob_approx' | 'phone' | 'id_card';

/** Result of a duplicate lookup: the distinct matching patients + which criteria fired. */
export interface DedupResult {
    matches: DedupCandidate[];
    reasons: DedupReason[];
}

/** Lowercase, strip accents (NFD + combining marks), collapse whitespace. */
export function normalizeName(input: unknown): string {
    if (typeof input !== 'string') return '';
    return input
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
}

/**
 * Digits-only phone string (strips spaces, dashes, parens, leading `+`, etc.).
 * Empty/non-string → ''. No minimum length — search uses this for partial queries
 * (SHER-579); dedup wraps it with a min-length gate via normalizePhone.
 */
export function phoneDigits(input: unknown): string {
    if (typeof input !== 'string') return '';
    return input.replace(/\D/g, '');
}

/**
 * Canonical phone key: keep digits only (strips spaces, dashes, parens, leading
 * `+` and any other separators). Returns '' for empty/non-string/too-short
 * values so blank or junk phone fields never collide into a false duplicate.
 */
export function normalizePhone(input: unknown): string {
    const digits = phoneDigits(input);
    return digits.length >= 6 ? digits : '';
}

/**
 * Canonical ID-card key: keep alphanumerics only, uppercase (strips spaces,
 * dashes, dots, slashes). Returns '' for empty/non-string/too-short values so
 * blank or junk ID fields never collide into a false duplicate.
 */
export function normalizeIdCard(input: unknown): string {
    if (typeof input !== 'string') return '';
    const cleaned = input.replace(/[^a-z0-9]/gi, '').toUpperCase();
    return cleaned.length >= 4 ? cleaned : '';
}

/** Shift a YYYY-MM-DD key by `delta` calendar days, or null if unparseable. */
function shiftDayKey(key: string, delta: number): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setDate(d.getDate() + delta);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
}

/** Canonical YYYY-MM-DD string for any supported birthdate shape, or null. */
export function dobKey(raw: unknown): string | null {
    const d = birthdateToDate(raw as any);
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function pushBucket(map: Map<string, DedupCandidate[]>, key: string, pat: DedupCandidate) {
    const bucket = map.get(key);
    if (bucket) bucket.push(pat);
    else map.set(key, [pat]);
}

/**
 * @param phoneFieldNames SHER-257: the personal-field `name`s to treat as phone
 *   numbers (primary `phone` + any tenant-custom secondaries). The caller derives
 *   this from the investigation's personalFields config. Omit/empty → no phone
 *   indexing (backward compatible).
 */
export function buildDedupIndex(
    patients: DedupCandidate[] | null | undefined,
    phoneFieldNames: string[] = [],
    idCardFieldNames: string[] = []
): DedupIndex {
    const byHealthId = new Map<string, DedupCandidate>();
    const byNameDob = new Map<string, DedupCandidate[]>();
    const byPhone = new Map<string, DedupCandidate[]>();
    const byIdCard = new Map<string, DedupCandidate[]>();

    if (!patients?.length) return { byHealthId, byNameDob, byPhone, byIdCard };

    for (const pat of patients) {
        const pd = pat?.personalData;
        if (!pd) continue;

        if (typeof pd.health_id === 'string' && pd.health_id.length > 0) {
            byHealthId.set(pd.health_id, pat);
        }

        const name = normalizeName(pd.name);
        const surname = normalizeName(pd.surnames);
        const dob = dobKey(pd.birthdate);
        if (name && surname && dob) {
            pushBucket(byNameDob, `${name}|${surname}|${dob}`, pat);
        }

        // Index every phone-typed field value under one shared phone map, so a
        // primary number can collide with a stored secondary (and vice versa).
        for (const fieldName of phoneFieldNames) {
            const phone = normalizePhone(pd[fieldName]);
            if (phone) pushBucket(byPhone, phone, pat);
        }

        // Index every ID-card-typed field value under one shared map.
        for (const fieldName of idCardFieldNames) {
            const idCard = normalizeIdCard(pd[fieldName]);
            if (idCard) pushBucket(byIdCard, idCard, pat);
        }
    }

    return { byHealthId, byNameDob, byPhone, byIdCard };
}

export interface CandidateInput {
    name?: string;
    surnames?: string;
    birthdate?: any;
    health_id?: string;
    [k: string]: any;
}

/**
 * SHER-257: returns every distinct existing patient that matches the candidate
 * on ANY criterion (health_id, name+surname+DOB, or any phone field), plus which
 * criteria fired — so the warning modal can show all matching patient cards and
 * the badges explaining why. Returns null when nothing matches.
 *
 * health_id stays a fast exact-match path; a health_id hit short-circuits to a
 * single match (different patients never legitimately share one).
 *
 * @param phoneFieldNames same list passed to buildDedupIndex; the candidate's
 *   values for these fields are checked against the shared phone index.
 */
export function findDuplicatePatient(
    index: DedupIndex,
    candidate: CandidateInput,
    phoneFieldNames: string[] = [],
    idCardFieldNames: string[] = []
): DedupResult | null {
    if (typeof candidate.health_id === 'string' && candidate.health_id.length > 0) {
        const hit = index.byHealthId.get(candidate.health_id);
        if (hit) return { matches: [hit], reasons: ['health_id'] };
    }

    // De-duplicate matches by uuid (one patient may fire several criteria) while
    // preserving discovery order; track which reasons actually produced a hit.
    const seen = new Set<string>();
    const matches: DedupCandidate[] = [];
    const reasons = new Set<DedupReason>();

    const addMatches = (bucket: DedupCandidate[] | undefined, reason: DedupReason) => {
        if (!bucket?.length) return;
        reasons.add(reason);
        for (const pat of bucket) {
            if (!seen.has(pat.uuid)) {
                seen.add(pat.uuid);
                matches.push(pat);
            }
        }
    };

    const name = normalizeName(candidate.name);
    const surname = normalizeName(candidate.surnames);
    const dob = dobKey(candidate.birthdate);
    if (name && surname && dob) {
        addMatches(index.byNameDob.get(`${name}|${surname}|${dob}`), 'name_dob');
        // SHER-257 (follow-up): also flag a ±1-day DOB typo (e.g. day "02" vs "03")
        // with the same name+surname, under the distinct name_dob_approx reason.
        for (const delta of [-1, 1]) {
            const shifted = shiftDayKey(dob, delta);
            if (shifted) addMatches(index.byNameDob.get(`${name}|${surname}|${shifted}`), 'name_dob_approx');
        }
    }

    for (const fieldName of phoneFieldNames) {
        const phone = normalizePhone(candidate[fieldName]);
        if (phone) addMatches(index.byPhone.get(phone), 'phone');
    }

    // ID card fires independently of name/DOB — same ID = same person.
    for (const fieldName of idCardFieldNames) {
        const idCard = normalizeIdCard(candidate[fieldName]);
        if (idCard) addMatches(index.byIdCard.get(idCard), 'id_card');
    }

    return matches.length ? { matches, reasons: Array.from(reasons) } : null;
}
